/*
* Copyright (c) 2025 Puratos. All rights reserved.
*/

import {waitForConsentPresent} from '../../core';
import {EcommerceDataLayerEvent} from '../utils/dataLayerEvent';
import {userDataParser} from '../parsers/user';
import {selectUserId} from '../../../selectors/auth';
import {addToCartParser, requestPriceParser, selectItemParser, viewItemParser, viewPromotionParser} from '../parsers/gaParserPDP';
import {GA4EventType} from '../types/event';
import {GA4PageType} from '../types/page';
import {getCurrencyCode} from '../../../selectors/cart';
import {getStore} from '../../../config/redux.storage';
import {GA4ComponentType} from '../types/component';

interface IEventData {
    productData: any;
    userId?: string;
    currency?: string;
    componentType?: GA4ComponentType;
    quantity?: number;
}

const pushDataToDataLayer = (event: EcommerceDataLayerEvent | undefined) => {
    (window as any)?.dataLayer?.push(event);
};

const createViewItemEvent = ({productData, userId, currency}: IEventData): EcommerceDataLayerEvent | undefined => {
    if (!productData) {
        return undefined;
    }

    const store = getStore().getState();
    const user = userDataParser(userId, store, GA4PageType.product);
    const viewItem = viewItemParser(productData, currency);

    return new EcommerceDataLayerEvent(GA4EventType.VIEW_ITEM, user, viewItem);
};

const createSelectItemEvent = ({productData, userId, currency, componentType}: IEventData): EcommerceDataLayerEvent | undefined => {
    if (!productData) {
        return undefined;
    }

    const store = getStore().getState();
    const user = userDataParser(userId, store, GA4PageType.product);
    const selectItem = selectItemParser(productData, currency, componentType);

    return new EcommerceDataLayerEvent(GA4EventType.SELECT_ITEM, user, selectItem);
};

const createAddToCartEvent = ({productData, userId, currency, componentType, quantity}: IEventData): EcommerceDataLayerEvent | undefined => {
    if (!productData) {
        return undefined;
    }

    const store = getStore().getState();
    const user = userDataParser(userId, store, GA4PageType.product);
    const addToCart = addToCartParser(productData, currency, componentType, quantity);

    return new EcommerceDataLayerEvent(GA4EventType.ADD_TO_CART, user, addToCart);
};

const createPriceRequestEvent = ({productData, userId, componentType}: IEventData): EcommerceDataLayerEvent | undefined => {
    if (!productData) {
        return undefined;
    }

    const store = getStore().getState();
    const user = userDataParser(userId, store, GA4PageType.product);
    const priceRequest = requestPriceParser(productData, componentType);

    return new EcommerceDataLayerEvent(GA4EventType.PRICE_REQUEST, user, priceRequest);
};

const createViewPromotionEvent = ({productData, userId, currency, quantity}: IEventData): EcommerceDataLayerEvent | undefined => {
    if (!productData) {
        return undefined;
    }

    const store = getStore().getState();
    const user = userDataParser(userId, store, GA4PageType.product);
    const Promotions = viewPromotionParser(productData, currency, quantity);

    return new EcommerceDataLayerEvent(GA4EventType.VIEW_PROMOTION, user, Promotions);
};

/**
 * GA4 view_item
 * Measure when a user views a specific product page.
 */
export const pdpGA4TrackViewItem = (productData: any): void => {
    const store = getStore().getState();
    const userId = selectUserId(store);
    const currency = getCurrencyCode(store);
    const event = createViewItemEvent({productData, userId, currency});
    pushDataToDataLayer(event);
};

/**
 * GA4 view_item
 * Measure when an anonymous user views a specific product page.
 */
export const pdpGA4TrackGuestViewItem = (productData: any): void => {
    waitForConsentPresent(() => {
        const event = createViewItemEvent({productData});
        pushDataToDataLayer(event);
    });
};

/**
 * GA4 select_item
 * Measure when an user select a product.
 */
export const pdpGA4TrackSelectItem = (productData: any, componentType: GA4ComponentType): void => {
    waitForConsentPresent(() => {
        const store = getStore().getState();
        const userId = selectUserId(store);
        const currency = getCurrencyCode(store);
        const event = createSelectItemEvent({productData, userId, currency, componentType});
        pushDataToDataLayer(event);
    });
};

/**
 * GA4 add_to_cart
 * Measure when a user adds product to cart.
 */
export const pdpGA4TrackAddToCart = (productData: any, componentType: GA4ComponentType, quantity: number): void => {
    const store = getStore().getState();
    const userId = selectUserId(store);
    const currency = getCurrencyCode(store);
    const event = createAddToCartEvent({productData, userId, currency, componentType, quantity});
    pushDataToDataLayer(event);
};

/**
 * GA4 price_request
 * Measure when a user request for a price of product.
 */
export const pdpGA4TrackPriceRequest = (productData: any, componentType: GA4ComponentType): void => {
    const store = getStore().getState();
    const userId = selectUserId(store);
    const event = createPriceRequestEvent({productData, userId, componentType});
    pushDataToDataLayer(event);
};

/**
 * GA4 view_promotion
 * Measure when a user views product with promotion.
 */
export const pdpGA4ViewPromotion = (productData: any, quantity: number): void => {
    const store = getStore().getState();
    const userId = selectUserId(store);
    const currency = getCurrencyCode(store);
    const event = createViewPromotionEvent({productData, userId, currency, quantity});
    pushDataToDataLayer(event);
};
